import { getHeaders } from "../request-headers.ts";
import type {
  ActiveRingGamesCountResponse,
  AdminRingGameMapAssetsResponse,
  AdminRingGameRankingsResponse,
  DeleteRingGameIssueReportsResponse,
  DisableRingGameIssueReportGroupResponse,
  GetAdminRingGameIssueReportDetailsParams,
  GetAdminRingGameIssueReportSummariesParams,
  GetAdminRingGameRankingsParams,
  GetRingGameRankParams,
  ReportRingIssueResponse,
  RingGameHistoryRecord,
  RingGameIdInput,
  RingGameIssueReportDetailsResponse,
  RingGameIssueReportSummariesResponse,
  RingGameOverallStatsResponse,
  RingGamePlayCount,
  RingGameRankRecord,
  RingGameScoreDistributionBucket,
  StartRingGameResponse,
  TakeRingGameGuessInput,
  TakeRingGameGuessResponse,
} from "../../model";

import { cnapexApiFetch } from "../../../fetcher.ts";

export const getGetAdminActiveRingGamesCountUrl = () => {
  return `/api/admin/game/ring/active-games-count`;
};

export const getAdminActiveRingGamesCount = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<ActiveRingGamesCountResponse> => {
  return cnapexApiFetch<ActiveRingGamesCountResponse>(getGetAdminActiveRingGamesCountUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAdminRingGameIssueReportDetailsUrl = (
  params?: GetAdminRingGameIssueReportDetailsParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/admin/game/ring/issue-report-details?${stringifiedParams}`
    : `/api/admin/game/ring/issue-report-details`;
};

export const getAdminRingGameIssueReportDetails = async (
  params?: GetAdminRingGameIssueReportDetailsParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RingGameIssueReportDetailsResponse> => {
  return cnapexApiFetch<RingGameIssueReportDetailsResponse>(
    getGetAdminRingGameIssueReportDetailsUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getDeleteAdminRingGameIssueReportsByRingIdUrl = (ringId: string) => {
  return `/api/admin/game/ring/issue-report-groups/${ringId}/delete`;
};

export const deleteAdminRingGameIssueReportsByRingId = async (
  ringId: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<DeleteRingGameIssueReportsResponse> => {
  return cnapexApiFetch<DeleteRingGameIssueReportsResponse>(
    getDeleteAdminRingGameIssueReportsByRingIdUrl(ringId),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getDisableAdminRingGameIssueReportGroupUrl = (ringId: string) => {
  return `/api/admin/game/ring/issue-report-groups/${ringId}/disable`;
};

export const disableAdminRingGameIssueReportGroup = async (
  ringId: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<DisableRingGameIssueReportGroupResponse> => {
  return cnapexApiFetch<DisableRingGameIssueReportGroupResponse>(
    getDisableAdminRingGameIssueReportGroupUrl(ringId),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getGetAdminRingGameIssueReportSummariesUrl = (
  params?: GetAdminRingGameIssueReportSummariesParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/admin/game/ring/issue-report-summaries?${stringifiedParams}`
    : `/api/admin/game/ring/issue-report-summaries`;
};

export const getAdminRingGameIssueReportSummaries = async (
  params?: GetAdminRingGameIssueReportSummariesParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RingGameIssueReportSummariesResponse> => {
  return cnapexApiFetch<RingGameIssueReportSummariesResponse>(
    getGetAdminRingGameIssueReportSummariesUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getDeleteAdminRingGameIssueReportUrl = (id: string) => {
  return `/api/admin/game/ring/issue-reports/${id}/delete`;
};

export const deleteAdminRingGameIssueReport = async (
  id: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<DeleteRingGameIssueReportsResponse> => {
  return cnapexApiFetch<DeleteRingGameIssueReportsResponse>(
    getDeleteAdminRingGameIssueReportUrl(id),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getGetAdminRingGameMapAssetsUrl = () => {
  return `/api/admin/game/ring/map-assets`;
};

export const getAdminRingGameMapAssets = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<AdminRingGameMapAssetsResponse> => {
  return cnapexApiFetch<AdminRingGameMapAssetsResponse>(getGetAdminRingGameMapAssetsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAdminRingGameOverallStatsUrl = () => {
  return `/api/admin/game/ring/overall-stats`;
};

export const getAdminRingGameOverallStats = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RingGameOverallStatsResponse> => {
  return cnapexApiFetch<RingGameOverallStatsResponse>(getGetAdminRingGameOverallStatsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAdminRingGamePlayCountsUrl = () => {
  return `/api/admin/game/ring/play-counts`;
};

export const getAdminRingGamePlayCounts = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RingGamePlayCount[]> => {
  return cnapexApiFetch<RingGamePlayCount[]>(getGetAdminRingGamePlayCountsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAdminRingGameRankingsUrl = (params?: GetAdminRingGameRankingsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/admin/game/ring/rankings?${stringifiedParams}`
    : `/api/admin/game/ring/rankings`;
};

export const getAdminRingGameRankings = async (
  params?: GetAdminRingGameRankingsParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<AdminRingGameRankingsResponse> => {
  return cnapexApiFetch<AdminRingGameRankingsResponse>(getGetAdminRingGameRankingsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getGetAdminRingGameScoreDistributionUrl = () => {
  return `/api/admin/game/ring/score-distribution`;
};

export const getAdminRingGameScoreDistribution = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RingGameScoreDistributionBucket[]> => {
  return cnapexApiFetch<RingGameScoreDistributionBucket[]>(
    getGetAdminRingGameScoreDistributionUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getTakeRingGameGuessUrl = () => {
  return `/api/game/ring/guess`;
};

export const takeRingGameGuess = async (
  takeRingGameGuessInput: TakeRingGameGuessInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<TakeRingGameGuessResponse> => {
  return cnapexApiFetch<TakeRingGameGuessResponse>(getTakeRingGameGuessUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(takeRingGameGuessInput),
  });
};

export const getGetMyRingGameHistoryUrl = () => {
  return `/api/game/ring/history`;
};

export const getMyRingGameHistory = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RingGameHistoryRecord[]> => {
  return cnapexApiFetch<RingGameHistoryRecord[]>(getGetMyRingGameHistoryUrl(), {
    ...options,
    method: "GET",
  });
};

export const getEnterNextRingGameRoundUrl = () => {
  return `/api/game/ring/next-round`;
};

export const enterNextRingGameRound = async (
  ringGameIdInput: RingGameIdInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<ReportRingIssueResponse> => {
  return cnapexApiFetch<ReportRingIssueResponse>(getEnterNextRingGameRoundUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(ringGameIdInput),
  });
};

export const getGetRingGameRankUrl = (params?: GetRingGameRankParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/game/ring/rank?${stringifiedParams}`
    : `/api/game/ring/rank`;
};

export const getRingGameRank = async (
  params?: GetRingGameRankParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RingGameRankRecord[]> => {
  return cnapexApiFetch<RingGameRankRecord[]>(getGetRingGameRankUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getReportRingIssueAndRerollCurrentRoundUrl = () => {
  return `/api/game/ring/report-issue`;
};

export const reportRingIssueAndRerollCurrentRound = async (
  ringGameIdInput: RingGameIdInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<ReportRingIssueResponse> => {
  return cnapexApiFetch<ReportRingIssueResponse>(getReportRingIssueAndRerollCurrentRoundUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(ringGameIdInput),
  });
};

export const getStartRingGameUrl = () => {
  return `/api/game/ring/start`;
};

export const startRingGame = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<StartRingGameResponse> => {
  return cnapexApiFetch<StartRingGameResponse>(getStartRingGameUrl(), {
    ...options,
    method: "POST",
  });
};
